import { useEffect, useMemo, useRef } from 'react'

import { mapValues } from 'lodash'

import { isPromise } from '@subaio/utils'

import { useAsyncError } from '../useAsyncError'
import type { BaseStep } from './BaseStep'
import type { BoundHandlers } from './BoundHandlers'
import type { GetExtendedStateRef } from './GetExtendedStateRef'
import type { MatchingHandlers } from './MatchingHandlers'
import { type StepMetaCloseView, type StepMetaHistoryClear, StepMetaType } from './stepMeta'

export type { StepHandlerResult, StepMetaCloseView, StepMetaHistoryClear } from './stepMeta'
export { StepMetaType, stepMetaCloseView, stepMetaHistoryClear } from './stepMeta'

export type { BaseStep, BoundHandlers, GetExtendedStateRef, MatchingHandlers }

/**
 * Wrap a multiple step-returning handlers with logic that automatically sets
 * the step and handles async returns.
 *
 * To help with dynamic type-safety, any handler that returns something _other_
 * than a valid step will be typed as non-existant (even though it actually will
 * be returned), to easily identify attempts to use unsupported handlers.
 */
export const useStepHandlers = <
	TStep extends BaseStep,
	THandlers extends Record<string, (...args: any) => Awaitable<any>>,
>(
	step: TStep | undefined,
	setStep: (step: TStep | undefined) => void,
	handlers: THandlers,
	onBack: () => void = () => {},
): BoundHandlers<TStep, THandlers> => {
	const stepRef = useRef(step)
	const onError = useAsyncError()

	useEffect(() => {
		stepRef.current = step
	}, [step])

	return useMemo(
		() =>
			mapValues(handlers, (func) => (...args: Array<any>) => {
				try {
					const result = func(...args)

					if (isPromise(result)) {
						const { current } = stepRef
						setStep(undefined)
						return result.then(
							(step) => handleStep(step, setStep, onBack),
							(error) => {
								if (current) setStep(current)
								onError(error)
								throw error
							},
						)
					}

					handleStep(result, setStep, onBack)
				} catch (error) {
					onError(error)
					throw error
				}
			}) as any,
		[handlers, setStep, onError, onBack],
	)
}

const handleStep = <TStep extends BaseStep>(
	step: TStep | StepMetaHistoryClear<TStep> | StepMetaCloseView,
	setStep: (step: TStep | undefined) => void,
	onBack: () => void,
) => {
	switch (step.type) {
		case StepMetaType.HistoryClear:
			// Ignore history clear, no history here
			setStep((step as StepMetaHistoryClear<TStep>).step)
			break
		case StepMetaType.CloseView:
			onBack()
			break
		default:
			setStep(step as TStep)
	}
}
